import { useState } from 'react';
import styled from '@emotion/styled';
import { Trans, useTranslation } from 'react-i18next';

import { tokens, Text, Accordion, Link } from '@hotjar/hotsauce';
import { useCurrentUser } from '@hotjar/user';
import { useOrganizations } from '@hotjar/organization';
import type { IdentityProviderModel } from '@hotjar/webapp/domains/sso/model';
import { useIdpDisconnectionFlow } from '@hotjar/webapp/domains/sso/hooks/useIdpDisconnectionFlow';
import { useIdpConnectionFlow } from '@hotjar/webapp/domains/sso/hooks/useIdpConnectionFlow';

import { useHelpHotjarUrl } from '@hotjar/translations';
import { DisconnectOrganizationModals } from './DisconnectOrganizationModals';
import { ConnectOrganizationModals } from './ConnectOrganizationModals';
import { IdentityProviderListItem } from './IdentityProviderListItem';

interface Props {
  identityProviders: IdentityProviderModel[];
}

const CONTACT_SUPPORT_LINK = '/requests/new?ticket_form_id=1500000552002';

const HelpText = styled(Text)`
  margin-top: ${tokens.spacer.x3};
`;

export const IdentityProviderList = ({ identityProviders }: Props): JSX.Element => {
  const { t } = useTranslation();
  const helpHotjarUrl = useHelpHotjarUrl();
  const { data: user } = useCurrentUser();
  const { data: organizations } = useOrganizations(user?.id);
  const idpConnectionFlow = useIdpConnectionFlow();
  const idpDisconnectionFlow = useIdpDisconnectionFlow();
  const [shownSection, setShownSection] = useState(identityProviders.length === 1 ? 0 : undefined);

  return (
    <div data-testid="idp-list">
      <Text as="h5" textType="header5">
        {t('common:settings.sso.list-identity-providers.title')}
      </Text>
      <Text as="p" textType="subtitle2" textColor="text_light">
        {t('common:settings.sso.list-identity-providers.subtitle')}
      </Text>

      {identityProviders && (
        <Accordion sectionToShow={shownSection} onChange={setShownSection} setUncontrolled>
          {identityProviders.map((identityProvider) => (
            <IdentityProviderListItem
              key={identityProvider.id}
              identityProvider={identityProvider}
              onConnectOrganizationClick={idpConnectionFlow.selectIdpForConnection}
              onDisconnectOrganizationClick={
                idpDisconnectionFlow.selectOrganizationForDisconnection
              }
            />
          ))}
        </Accordion>
      )}

      <HelpText as="p" textType="body2">
        <Trans i18nKey="common:settings.sso.list-identity-providers.help">
          Need to make changes to your single-sign on settings?{' '}
          <Link href={`${helpHotjarUrl}${CONTACT_SUPPORT_LINK}`} kind="inline" isExternal>
            Contact support
          </Link>
        </Trans>
      </HelpText>

      {!!organizations?.length && (
        <>
          <ConnectOrganizationModals
            idpConnectionFlow={idpConnectionFlow}
            organizations={organizations}
          />
          <DisconnectOrganizationModals idpDisconnectionFlow={idpDisconnectionFlow} />
        </>
      )}
    </div>
  );
};
